import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
  ElementRef,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SessionManagerService } from '@ops/shared-types';
import { OpsDatePickerModule } from '../ops-date-picker/ops-date-picker.module';
import { OpsSelectComponent } from '../ops-select/ops-select.component';
import { OpsInputComponent } from '../ops-input/ops-input.component';

const zerosApplyOnDate = (date: Date) => {
  const zerosDate = new Date(date);
  zerosDate.setHours(0);
  zerosDate.setMinutes(0);
  zerosDate.setSeconds(0);
  zerosDate.setMilliseconds(0);

  return zerosDate;
};

const timestampFromDatePrev = (date: Date) => {
  const baseDMin = zerosApplyOnDate(new Date(date));
  baseDMin.setDate(0);

  return timestampFromDate(baseDMin);
};

const timestampFromDateNext = (date: Date) => {
  const baseDMin = zerosApplyOnDate(new Date(date));
  baseDMin.setMonth(baseDMin.getMonth() + 1);
  baseDMin.setDate(0);
  // baseDMin.setMonth(baseDMin.getMonth() + 1);

  return timestampFromDate(baseDMin);
};

const timestampFromDate = (date: Date): number => {
  return zerosApplyOnDate(date).getTime();
};

@Component({
  selector: 'ops-calendar',
  standalone: true,
  imports: [
    CommonModule,
    OpsInputComponent,
    OpsSelectComponent,
    OpsDatePickerModule,
    DatePipe,
    TranslateModule,
  ],
  templateUrl: './ops-calendar.component.html',
  styleUrl: './ops-calendar.component.scss',
})
export class OpsCalendarComponent implements OnInit, OnDestroy {
  private _date?: Date;

  private _minDate: Date = zerosApplyOnDate(new Date('1950-01-01'));
  private _minDay: number = this._minDate.getDate();
  private _minMonth: number = this._minDate.getMonth();
  private _minYear: number = this._minDate.getFullYear();
  private _minTsDate: number = this._minDate.getTime() - 86400000;

  @Input() set minDate(date: Date) {
    this._minDate = zerosApplyOnDate(new Date(date));

    this._minDay = this._minDate.getDate();
    this._minMonth = this._minDate.getMonth();
    this._minYear = this._minDate.getFullYear();
    this._minTsDate = this._minDate.getTime() - 86400000;
  }
  get minDate() {
    return this._minDate;
  }

  @Input() noteText?: string;
  @Input() mandatory?: boolean;
  @Input() isDisabled?: boolean;

  @Input() invertDirection = false;

  private _maxDate: Date = zerosApplyOnDate(new Date('2034-01-31'));
  private _maxDay: number = this._maxDate.getDate();
  private _maxMonth: number = this._maxDate.getMonth();
  private _maxYear: number = this._maxDate.getFullYear();
  private _maxTsDate: number = this._maxDate.getTime();

  @Input() set maxDate(date: Date) {
    this._maxDate = zerosApplyOnDate(new Date(date));

    this._maxDay = this._maxDate.getDate();
    this._maxMonth = this._maxDate.getMonth();
    this._maxYear = this._maxDate.getFullYear();
    this._maxTsDate = this._maxDate.getTime();
  }
  get maxDate() {
    return this._maxDate;
  }

  isInvalidDate = (valToCheck: any) => {
    return valToCheck instanceof Date && isNaN(valToCheck.getTime());
  };

  @Input() set date(val: Date | string | null) {
    if (
      val !== undefined &&
      val !== null &&
      val !== '' &&
      !this.isInvalidDate(val)
    ) {
      if (val instanceof Date) {
        this._date = val;
      } else if (val?.split('T')?.length > 1) {
        this._date = new Date(val);
      }
    } else {
      this._date = null;
    }
  }

  get date() {
    return this._date;
  }

  // get datePreview(): string {
  //   if (this._date) {
  //     const dateCover = this._date.getDate();
  //     const monthCover = this._date.getMonth() + 1;

  //     return (
  //       (dateCover < 10 ? '0' + dateCover : dateCover) +
  //       '.' +
  //       (monthCover < 10 ? '0' + monthCover : monthCover) +
  //       '.' +
  //       this._date.getFullYear()
  //     );
  //   }

  //   return '';
  // }

  @Input() label?: string;

  @Input() formName: string;

  @Input() isHideIcon: boolean = false;

  @Input() isPreview?: boolean;
  @Output() validationError = new EventEmitter<string>();
  @Output() dateChanged: EventEmitter<string> = new EventEmitter<string>();

  isOpen = false;
  subscriptions: Array<Subscription> = [];

  constructor(
    private sessionManager: SessionManagerService,
    private el: ElementRef,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    //TODO: move this to app-component to avoid showing login screen unnessessarily
    // also talk to design about having a "welcome back" message displayed
    // this.loadingService.startLoadingSpinner();
    // this.sessionManager.attemptResumeSession();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  // get selectedYearItems() {
  //   if (this.selectedYear) return [this.selectedYear + ''];

  //   return [];
  // }

  @HostListener('document:keydown', ['$event']) onKeyDown(e: any) {
    if (e.key === 'Enter') {
      return;
    }
  }

  onFocus() {
    this.openDatePicker();
  }

  onBlur() {
    if (this.isOpen) {
      this.el.nativeElement.getElementsByTagName('input')[0].select();
    }
  }

  onClick() {
    //
  }

  onMouseDown() {
    //
  }

  onMouseUp() {
    //
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Tab') {
      if (this.isOpen) {
        this.isOpen = false;
      }
      return;
    }
  }

  onErrorHandler(error: string, field?: string) {
    if (!error || error === '') {
      // delete this.errorsTrack[field];
    } else {
      // this.errorsTrack[field] = error;
    }
  }

  openDatePicker() {
    this.isOpen = true;
    this.el.nativeElement.getElementsByTagName('input')[0].select();
  }

  onDatePickerClosed() {
    this.isOpen = false;
    this.el.nativeElement.getElementsByTagName('input')[0].blur();
  }

  get selectedDates(): Array<Date> {
    return this._date ? [this._date] : [];
  }

  onTogglePreparationDate(datesSelected: { date: Date; cleared?: boolean }) {
    if (datesSelected?.cleared) {
      this._date = null;
      this.onDatePickerClosed();
      this.dateChanged.emit('');
      return;
    }

    const month = datesSelected.date.getMonth() + 1;
    const dateV = datesSelected.date.getDate();
    const dateSelected =
      [
        datesSelected.date.getFullYear(),
        month < 10 ? '0' + month : month,
        dateV < 10 ? '0' + dateV : dateV,
      ].join('-') + 'T00:00:00.000';
    // console.log(dateSelected);
    // console.log('DATES SELECTED', new Date(dateSelected));
    this._date = new Date(dateSelected);
    this.dateChanged.emit(dateSelected);
    this.onDatePickerClosed();
  }

  // Get the current language for date formatting
  getCurrentLanguage(): string {
    return this.translateService.currentLang || 'en';
  }

  // Check if current language is Japanese
  isJapanese(): boolean {
    return this.getCurrentLanguage() === 'jp';
  }

  // Format date for display (alternative to dd.MM.yyyy format)
  formatDateForDisplay(date: Date): string {
    if (!date) return '';
    
    const currentLang = this.getCurrentLanguage();
    if (currentLang === 'jp') {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}${this.translateService.instant('dateFormatting.yearFormat')}${month}${this.translateService.instant('dateFormatting.monthFormat')}${day}日`;
    }
    
    // For other languages, use standard format
    return date.toLocaleDateString();
  }

  // Format year for display
  formatYearForDisplay(date: Date): string {
    if (!date) return '';
    
    const year = date.getFullYear();
    const currentLang = this.getCurrentLanguage();
    if (currentLang === 'jp') {
      return `${year}${this.translateService.instant('dateFormatting.yearFormat')}`;
    }
    return year.toString();
  }

  // Format month for display
  formatMonthForDisplay(date: Date): string {
    if (!date) return '';
    
    const month = date.getMonth() + 1;
    const currentLang = this.getCurrentLanguage();
    if (currentLang === 'jp') {
      return `${month}${this.translateService.instant('dateFormatting.monthFormat')}`;
    }
    
    // For other languages, use month names
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return monthNames[date.getMonth()];
  }
}
